import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api, { uploadLogo, getEvidenceDisplayUrl } from '../../../lib/api';
import { useAuthStore } from '../../../store/authStore';
import { isAdmin } from '../../../lib/roles';
import SettingsCard from '../../../components/ui/SettingsCard';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import Button from '../../../components/ui/Button';
import AvatarUploader from '../../../components/ui/AvatarUploader';
import { compressBrandImage } from '../../../lib/imageCompression';

const initialForm = {
  name: '',
  licence_number: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  logo_url: '',
};

export default function Settings() {
  // Hooks only at top – no early return before any hook (avoids "Rendered more hooks" crash)
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = user?.organizationId ?? user?.organization_id;
  const canEditSettings = isAdmin(user);

  const [formData, setFormData] = useState(initialForm);
  const [logoUploading, setLogoUploading] = useState(false);

  const { data: org, isLoading, isError, error } = useQuery(
    ['organization', orgId],
    () => api.get(`/organizations/${orgId}`).then((res) => res.data),
    { enabled: !!orgId }
  );

  useEffect(() => {
    if (!org) return;
    setFormData({
      name: org.name ?? '',
      licence_number: org.licence_number ?? '',
      address: org.address ?? '',
      phone: org.phone ?? '',
      email: org.email ?? '',
      website: org.website ?? '',
      logo_url: org.logo_url ?? '',
    });
  }, [org]);

  useEffect(() => {
    if (!canEditSettings && orgId) {
      navigate('/settings/profile', { replace: true });
    }
  }, [canEditSettings, orgId, navigate]);

  const handleLogoUpload = async (file) => {
    setLogoUploading(true);
    try {
      // Brand path: PNG so a transparent logo stays transparent on the report
      // header, plus a size cap and EXIF orientation bake-in. Never the evidence
      // JPEG path, which would matte the logo onto a white rectangle.
      const prepared = await compressBrandImage(file);
      const url = await uploadLogo(prepared);
      setFormData((prev) => ({ ...prev, logo_url: url }));
      queryClient.setQueryData(['organization', orgId], (prev) => (prev ? { ...prev, logo_url: url } : prev));
      toast.success('Logo uploaded');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Logo upload failed');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = () => {
    setFormData((prev) => ({ ...prev, logo_url: '' }));
    toast('Logo cleared — click Save Changes to apply');
  };

  const updateOrgMutation = useMutation(
    (newData) => api.put(`/organizations/${orgId}`, newData).then((res) => res.data),
    {
      onSuccess: (data) => {
        toast.success('Settings updated successfully');
        queryClient.setQueryData(['organization', orgId], data);
      },
      onError: (err) => {
        console.error(err);
        const validationMessage = Array.isArray(err?.response?.data?.errors)
          ? err.response.data.errors.map((item) => item.msg).filter(Boolean).join(', ')
          : '';
        toast.error(validationMessage || err?.response?.data?.error || 'Failed to update settings');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    updateOrgMutation.mutate(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (isLoading) {
    return (
      <div className="page" style={{ padding: '2rem', textAlign: 'center' }}>
        <p className="text-muted">Loading settings…</p>
      </div>
    );
  }

  if (!orgId) {
    return (
      <div className="page" style={{ padding: '2rem', textAlign: 'center' }}>
        <p className="text-muted">Organisation context required.</p>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="page" style={{ padding: '2rem', textAlign: 'center' }}>
        <p className="text-muted">Failed to load organisation settings.</p>
      </div>
    );
  }

  if (!canEditSettings) {
    return null; /* redirecting to /settings/my-settings */
  }

  return (
    <div className="page settings-page">
      <header className="settings-tab-header">
        <h1 className="settings-tab-title">Organisation Settings</h1>
        <p className="text-muted">
          These details will appear on PDF reports generated for your clients.
        </p>
      </header>

      <SettingsCard>
        <form onSubmit={handleSubmit}>
          {/* Branding: Logo, Name, Licence Number */}
          <section className="settings-section settings-branding">
            <h2 className="settings-section-title">Branding</h2>
            <div className="form-group">
              <label className="form-label">Logo</label>
              {/* Preview uses public URL from organization-logos bucket (Supabase); backend uses SUPABASE_URL / SUPABASE_SERVICE_KEY from .env */}
              <AvatarUploader
                shape="square"
                label="logo"
                alt="Organisation logo"
                src={getEvidenceDisplayUrl(formData.logo_url)}
                busy={logoUploading}
                onUpload={handleLogoUpload}
                onRemove={handleLogoRemove}
              />
            </div>
            <Input
              id="org-name"
              label="Company Name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Smith Electrical Services"
              required
            />
            <Input
              id="org-licence"
              label={<>Licence Number <small>(Required for PDF reports)</small></>}
              type="text"
              name="licence_number"
              value={formData.licence_number}
              onChange={handleChange}
              placeholder="e.g. 123456C"
            />
          </section>

          {/* Contact details: Address, Phone, Email, Website */}
          <section className="settings-section settings-contact">
            <h2 className="settings-section-title">Contact details</h2>
            <div className="settings-contact-grid">
              <div className="settings-left">
                <div className="form-row">
                  <Input
                    id="org-phone"
                    label="Phone"
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  <Input
                    id="org-email"
                    label="Email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <Input
                  id="org-website"
                  label="Website"
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://"
                />
              </div>
              <div className="settings-right">
                <Textarea
                  id="org-address"
                  label="Address"
                  className="full-height"
                  textareaClassName="address-input"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Industry Drive..."
                />
              </div>
            </div>
          </section>

          {canEditSettings && (
            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                disabled={updateOrgMutation.isLoading || logoUploading}
              >
                {updateOrgMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </form>
      </SettingsCard>
    </div>
  );
}
